import { PlusIcon, UsersIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Panel, PanelHeader } from '@/components/core/Panel';
import { PanelBody } from '@/components/core/Panel/PanelBody';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal';
import { useOrganization } from '@/hooks/useOrganization';
import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

import { BuyLicensesDialog } from './components/BuyLicensesDialog';
import { CreateUserDialog } from './components/CreateUserDialog';
import { EditUserDialog } from './components/EditUserDialog';
import { LicensesBar } from './components/LicensesBar';
import { UsersTable } from './components/UsersTable';
import {
    displayRoles as composeDisplayRoles,
    isSystemRolesVisible,
    licenseUserCount,
    systemRoleNames,
} from './user-management.format';
import {
    downloadUsersFile,
    refreshUserManagementLists,
    useDeactivateUser,
    useDeleteInvitation,
    usePendingInvitations,
    useRestrictionFilters,
    useRoleDefinitions,
    useUpdateInvitation,
    useUsageLimits,
    useUsers,
} from './user-management.queries';
import { PendingInvitation, UserAccount } from './user-management.types';

type ConfirmTarget =
    | { kind: 'user'; user: UserAccount }
    | { kind: 'invitation'; invitation: PendingInvitation };

type HopscotchWindow = Window & {
    hopscotch?: { getCurrTour?: () => unknown };
};

export default function UserManagementPage() {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { user } = useUser();
    const { organization } = useOrganization();
    const usersQuery = useUsers({ variables: { currentUserId: String(user?.userId ?? '') } });
    const invitationsQuery = usePendingInvitations();
    const limitsQuery = useUsageLimits();
    const definitionsQuery = useRoleDefinitions();
    const filtersQuery = useRestrictionFilters();
    const deactivateUser = useDeactivateUser();
    const deleteInvitation = useDeleteInvitation();
    const updateInvitation = useUpdateInvitation();

    const [createOpen, setCreateOpen] = useState(false);
    const [editUser, setEditUser] = useState<UserAccount | undefined>();
    const [buyOpen, setBuyOpen] = useState(false);
    const [confirm, setConfirm] = useState<ConfirmTarget | undefined>();
    const [exporting, setExporting] = useState(false);
    const [userLimitOverride, setUserLimitOverride] = useState<number | undefined>();

    const users = usersQuery.data ?? [];
    const invitations = invitationsQuery.data ?? [];
    const userLimit = userLimitOverride ?? limitsQuery.data?.limits?.USERS ?? 0;
    const showBuyLicenses = userLimit > 0;
    const showSystemRoles = isSystemRolesVisible(
        getRootScope(getInjector())?.systemSettings?.UI_SHOW_SYSTEM_ROLES,
    );
    const systemRoles = systemRoleNames(definitionsQuery.data ?? {}, organization, showSystemRoles);
    const licensesUsedText = t('USER_MANAGEMENT.LICENSES_USED', {
        numberOfUsers: licenseUserCount(users, invitations.length),
        userLimit,
    });

    const refreshFromChannel = useCallback(() => {
        const tour = (window as HopscotchWindow).hopscotch?.getCurrTour?.();
        if (tour) {
            return;
        }
        refreshUserManagementLists();
    }, []);

    useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.USERGROUPS_CHANGED,
        eventHandler: refreshFromChannel,
    });

    useEffect(() => {
        const root = getRootScope(getInjector());
        const unsubscribe = root?.$on?.('userAccountUpdated', () => {
            refreshUserManagementLists();
        });
        return () => unsubscribe?.();
    }, []);

    const onExport = async () => {
        setExporting(true);
        try {
            await downloadUsersFile();
        } catch (error) {
            logger.error(error);
        } finally {
            setExporting(false);
        }
    };

    const onConfirm = () => {
        if (!confirm) {
            return;
        }
        if (confirm.kind === 'user') {
            deactivateUser.mutate({ accountId: confirm.user.id }, {
                onSuccess: () => {
                    toast.success('USER_MANAGEMENT.MESSAGE_USER_REMOVED');
                    refreshUserManagementLists();
                    setConfirm(undefined);
                },
            });
            return;
        }
        deleteInvitation.mutate({ invitationId: confirm.invitation.id }, {
            onSuccess: () => {
                toast.success('INVITATION_DELETED');
                refreshUserManagementLists();
                setConfirm(undefined);
            },
        });
    };

    const onResend = (invitation: PendingInvitation) => {
        updateInvitation.mutate(invitation, {
            onSuccess: () => {
                toast.success('INVITATION_UPDATED');
                refreshUserManagementLists();
            },
        });
    };

    return (
        <div className="tw-flex tw-min-w-0 tw-w-full tw-max-w-full tw-flex-col">
            <PanelHeader
                id="user-management-heading"
                className="tw-shrink-0 !tw-pb-4"
                icon={UsersIcon}
                title={t('USER_MANAGEMENT.USER_MANAGEMENT')}
                actions={(
                    <Button
                        id="new-user-btn"
                        variant="default"
                        hasIcon
                        icon={PlusIcon}
                        className="tw-shrink-0"
                        onClick={() => setCreateOpen(true)}
                    >
                        {t('USER_MANAGEMENT.NEW_USER')}
                    </Button>
                )}
            />
            <div className="user-management-panel tw-min-w-0 tw-w-full tw-max-w-full">
                <Panel>
                    <PanelBody className="tw-h-auto tw-min-h-0 tw-min-w-0 tw-max-w-full tw-overflow-x-auto">
                        <LicensesBar
                            showBuyLicenses={showBuyLicenses}
                            licensesUsedText={showBuyLicenses ? licensesUsedText : undefined}
                            exporting={exporting}
                            onBuyLicenses={() => setBuyOpen(true)}
                            onExport={onExport}
                            buyLabel={t('USER_MANAGEMENT.BUY_USER_LICENSE')}
                            exportLabel={t('EXPORT_USER.BUTTON_TEXT')}
                        />
                        <UsersTable
                            users={users}
                            pendingInvitations={invitations}
                            isLoading={usersQuery.isLoading}
                            roleMetadataByName={definitionsQuery.data ?? {}}
                            filters={filtersQuery.data ?? []}
                            displayRoles={(account) => composeDisplayRoles(account.roles, systemRoles)}
                            labels={{
                                user: t('USER_MANAGEMENT.USER_NAME'),
                                roles: t('USER_MANAGEMENT.ROLES'),
                                restrictionFilter: t('USER_MANAGEMENT.RESTRICTION_FILTER'),
                                restrictionQuery: t('USER_MANAGEMENT.RESTRICTION_QUERY'),
                                actions: t('USER_MANAGEMENT.ACTIONS'),
                                empty: t('USER_MANAGEMENT.NO_USERS_AVAILABLE'),
                                systemUser: t('USER_MANAGEMENT.SYSTEM_USER'),
                                managingUser: t('USER_MANAGEMENT.MANAGING_USER'),
                                lastLoggedIn: (lastLogin) => t('USER_MANAGEMENT.LAST_LOGGED_IN', { lastLogin }),
                                editUser: t('USER_MANAGEMENT.EDIT_USER'),
                                deleteUser: t('USER_MANAGEMENT.DELETE_USER'),
                                resend: t('INVITATION.RESEND_INVITATION.LABEL'),
                                deleteInvitation: t('INVITATION.DELETE_INVITATION.LABEL'),
                            }}
                            onEdit={setEditUser}
                            onDeleteUser={(account) => setConfirm({ kind: 'user', user: account })}
                            onResend={onResend}
                            onDeleteInvitation={(invitation) => setConfirm({ kind: 'invitation', invitation })}
                        />
                    </PanelBody>
                </Panel>
            </div>
            {createOpen && (
                <CreateUserDialog
                    open
                    onClose={() => {
                        setCreateOpen(false);
                        refreshUserManagementLists();
                    }}
                />
            )}
            {editUser && (
                <EditUserDialog
                    open
                    user={editUser}
                    onClose={() => setEditUser(undefined)}
                />
            )}
            {buyOpen && (
                <BuyLicensesDialog
                    open
                    onClose={(limit) => {
                        setBuyOpen(false);
                        if (limit != null) {
                            setUserLimitOverride(limit);
                        }
                    }}
                />
            )}
            <ConfirmationModal
                show={Boolean(confirm)}
                title={confirm?.kind === 'invitation'
                    ? 'INVITATION.DELETE_INVITATION.LABEL'
                    : 'USER_MANAGEMENT.DELETE_USER'}
                subTitle={confirm?.kind === 'invitation'
                    ? 'INVITATION.DELETE_INVITATION.TEXT'
                    : 'USER_MANAGEMENT.REMOVE_USER'}
                onSubmit={onConfirm}
                onClose={() => setConfirm(undefined)}
            />
        </div>
    );
}
